import {
  ChangeDetectorRef,
  computed,
  DestroyRef,
  Directive,
  inject,
  Injector,
  input,
  type InputSignal,
  linkedSignal,
  OnInit,
  type Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors } from '@angular/forms';
import { merge } from 'rxjs';
import { ControlType } from './types';
import { TooltipColor, TooltipPosition } from '../controls/tooltip/tooltip';

/**
 * HxBaseControl
 *
 * A generic base directive for all Harmony controls providing common properties and behaviors:
 *
 * - **disabled**: boolean - whether the control is disabled (not interactive)
 * - **cssStyle**: string - custom CSS styles to apply to the component
 * - **show**: boolean - visibility control (show/hide the component)
 * - **id**: string - unique identifier for the control
 * - **cssClass**: string - additional CSS classes to apply
 * - **ariaLabel**: string - accessible label for screen readers
 * - **tabIndex**: number - keyboard navigation order
 * - **ariaLabelledby**: string - ID of element that labels this control (WCAG 2.2 AA)
 * - **ariaDescribedby**: string - ID of element that describes this control (WCAG 2.2 AA)
 * - **error**: boolean - whether the control is in an error/invalid state
 * - **placeholder**: string - hint text displayed when control is empty
 * - **label**: string - descriptive label text for the control
 * - **hidden**: boolean - HTML hidden attribute
 * - **title**: string - tooltip/title attribute
 * - **showInDesigner**: boolean - whether the control should be shown in design tools
 * - **designMode**: boolean - whether the control is in design/builder mode
 * - **controlType**: ControlType - classification of the control (BasicControl, LayoutControl, ClinicalControl)
 * - **canDragDrop**: boolean - whether the control can be dragged and dropped in design tools
 * - **canSelect**: boolean - whether the control can be selected in the designer
 * - **canMove**: boolean - whether the control can be moved in the designer
 * - **canResize**: boolean - whether the control can be resized in the designer
 * - **isLocked**: boolean - whether the control is locked (prevents editing)
 * - **canDelete**: boolean - whether the control can be deleted from the designer
 * - **canDuplicate**: boolean - whether the control can be duplicated
 * - **designerLabel**: string - display name shown in designer palette
 * - **designerCategory**: string - category for grouping in palette
 * - **designerTags**: string[] - tags for searching/filtering
 * - **designerIcon**: string - icon name/class for palette display
 * - **designerDescription**: string - description/tooltip shown in designer
 * - **designerHelpUrl**: string - help text or documentation link
 * - **minWidth/maxWidth**: string - size constraints for width
 * - **minHeight/maxHeight**: string - size constraints for height
 * - **defaultWidth/defaultHeight**: string - preferred/default size
 * - **gridColumn/gridRow**: string - grid layout positioning
 * - **flexOrder**: number - flex order for flexbox layouts
 * - **snapToGrid**: boolean - whether to snap to grid when moving
 * - **gridSize**: number - grid size for snapping (in pixels)
 * - **showInPreview**: boolean - whether to show in preview mode
 * - **showInRuntime**: boolean - whether to show in runtime (production)
 * - **designerPlaceholder**: string - placeholder content shown in designer
 * - **required**: boolean - whether this control is required in the form
 * - **validationRules**: Record<string, any> - custom validation rules (JSON serializable)
 * - **readonly**: boolean - whether control is read-only
 * - **designerConfig**: Record<string, any> - configuration data for template storage
 * - **configurable**: boolean - whether this control's configuration can be edited
 * - **defaultConfig**: Record<string, any> - default configuration values for new instances
 *
 * This directive provides a consistent interface across all controls and centralizes common properties to reduce
 * duplication in individual component implementations.
 *
 * @example
 *   ```typescript
 *   // Extend this directive in your control
 *   export class HxMyControl extends HxBaseControl {
 *     // Component-specific code here
 *   }
 *   ```;
 *
 * @internal Not exported - internal base for components only
 * @see specs/harmony-design-system
 */
/**
 * Base class for shared ControlValueAccessor functionality. Provides common form integration patterns for all form
 * controls.
 *
 * Note: Validator interface is NOT implemented on this base class to avoid circular dependency issues. Controls that
 * need validation should implement Validator directly and provide NG_VALIDATORS.
 *
 * Subclasses must implement:
 *
 * - _setValue(value): void
 * - _validate(control): ValidationErrors | null (optional, for controls implementing Validator)
 */
interface ControlValueAccessorImpl extends ControlValueAccessor {
  /**
   * Abstract method: set value from form model (ControlValueAccessor) Subclasses override to set component value from
   * external source
   */
  _setValue(value: any): void;

  /** Abstract method: validate the control value. Subclasses can override to implement custom validation */
  _validate(control: AbstractControl): ValidationErrors | null;
}

@Directive()
export class HxBaseControl implements ControlValueAccessor, OnInit {
  protected readonly _changeDetectorRef = inject(ChangeDetectorRef);
  protected readonly _destroyRef = inject(DestroyRef);
  private readonly _injector = inject(Injector);
  protected ngControl: NgControl | null = null;

  /** Callbacks registered by ControlValueAccessor Called to notify form control of value/touched changes */
  protected _onChange: (value: any) => void = () => {};
  protected _onTouched: () => void = () => {};
  protected _validatorChangeFn: () => void = () => {};

  ngOnInit(): void {
    this.ngControl = this._injector.get(NgControl, null, { self: true, optional: true });
    if (this.ngControl && this.ngControl.valueAccessor !== this) {
      this.ngControl.valueAccessor = this;
    }
  }

  /**
   * Whether the control is disabled and not interactive.
   *
   * When true:
   *
   * - User interaction is prevented
   * - Visual opacity is reduced (typically 50%)
   * - Control is skipped in keyboard tab order
   * - ARIA attribute aria-disabled is set to true
   *
   * @default false
   */
  readonly disabled = input<boolean | undefined>(false);

  /**
   * Internal writable signal tracking disabled state from ControlValueAccessor.setDisabledState()
   *
   * @internal
   */
  protected readonly _disabledFromCVA = signal<boolean>(false);

  /**
   * Computed disabled state that combines input and CVA-driven changes using linkedSignal This provides more explicit
   * state synchronization than computed()
   */
  readonly disabledState = linkedSignal(() => this._disabledFromCVA() || this.disabled());

  /**
   * Custom CSS styles to apply directly to the component.
   *
   * @example
   *   ```html
   *   <hx-button [cssStyle]="'color: red; margin: 10px;'"></hx-button>
   *   ```;
   *
   * @default ''
   */
  readonly cssStyle: InputSignal<string> = input<string>('');

  /**
   * Controls the visibility of the component (show/hide).
   *
   * When false, the component is hidden but still takes up space in the DOM. For complete removal, use *ngIf instead.
   *
   * @default true
   */
  readonly show: InputSignal<boolean> = input<boolean>(true);

  /**
   * Unique identifier for the control.
   *
   * Used for:
   *
   * - HTML id attribute
   * - Label association (for attribute)
   * - Form control name
   * - ARIA relationships
   *
   * @default undefined
   */
  readonly id: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Protected method to access the id signal value. Useful for child classes that need to override id for interface
   * compliance.
   *
   * @internal
   */
  protected getIdValue(): string | undefined {
    return this.id();
  }

  /**
   * Additional CSS classes to apply to the component.
   *
   * @example
   *   ```html
   *   <hx-button [cssClass]="'custom-button danger'"></hx-button>
   *   ```;
   *
   * @default ''
   */
  readonly cssClass: InputSignal<string> = input<string>('');

  /**
   * Accessible label for screen readers and tooltip.
   *
   * Used for:
   *
   * - Aria-label attribute
   * - Screen reader announcements
   * - Tooltip text for icon-only controls
   *
   * @default undefined
   */
  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined);
  /**
   * ID of the element that labels this control.
   *
   * Used when there's a visible label element that you want to associate with the control via ID reference. Critical
   * for WCAG 2.2 AA compliance when label is visually separated from control.
   *
   * @example
   *   ```html
   *   <label id="name-label">Full Name</label>
   *   <hx-input [ariaLabelledby]="'name-label'"></hx-input>
   *   ```;
   *
   * @default undefined
   */
  readonly ariaLabelledby: InputSignal<string | undefined> = input<string | undefined>(undefined, {
    alias: 'aria-labelledby',
  });

  /**
   * ID of the element that describes this control.
   *
   * Used to associate additional descriptive text (hints, error messages, instructions) with the control. Critical for
   * WCAG 2.2 AA compliance to provide context for assistive technology users.
   *
   * Automatically managed by HxFormGroup for error/hint text, but can be manually set for custom descriptions.
   *
   * @example
   *   ```html
   *   <hx-input [ariaDescribedby]="'password-hint'"></hx-input>
   *   <p id="password-hint">Must be at least 8 characters</p>
   *   ```;
   *
   * @default undefined
   */
  readonly ariaDescribedby: InputSignal<string | undefined> = input<string | undefined>(undefined, {
    alias: 'aria-describedby',
  });

  /**
   * Indicates whether the control is in an error/invalid state.
   *
   * When true:
   *
   * - Visual error styling is applied (usually red border/background)
   * - Often displays error icons
   * - Can be combined with form validation results
   * - Communicates to users that the control needs attention
   *
   * @example
   *   ```html
   *   <hx-input [error]="emailForm.get('email')?.invalid"></hx-input>
   *   ```;
   *
   * @default false
   */
  readonly error: InputSignal<boolean> = input<boolean>(false);

  /**
   * Hint text displayed when the control is empty.
   *
   * Provides a visual cue to users about what type of input is expected. This text disappears when the user starts
   * typing or selects a value.
   *
   * @example
   *   ```html
   *   <hx-input [placeholder]="'Enter your email address'"></hx-input>
   *   <hx-dropdown [placeholder]="'Select an option'"></hx-dropdown>
   *   ```;
   *
   * @default '' (empty string)
   */
  readonly placeholder: InputSignal<string> = input<string>('');
  /**
   * Descriptive label text for the control.
   *
   * Used to display a label associated with the control. Can be used in addition to ariaLabel for both visual and
   * accessibility purposes.
   *
   * @example
   *   ```html
   *   <hx-input [label]="'Email Address'"></hx-input>
   *   <hx-checkbox [label]="'I agree to terms'"></hx-checkbox>
   *   ````;
   *
   * @default '' (empty string)
   */
  readonly label: InputSignal<string> = input<string>('');
  /**
   * Keyboard navigation tab index.
   *
   * - Positive number: Control is focusable in tab order
   * - 0: Control is focusable but tab order is determined by document order
   * - -1: Control is not in tab order but can be focused programmatically
   *
   * @default 0
   */
  readonly tabIndex: InputSignal<number> = input<number>(0);

  /**
   * HTML hidden attribute.
   *
   * When true, the element is hidden from display and completely removed from accessibility tree. Similar to
   * display:none but using the native HTML attribute.
   *
   * @default false
   */
  readonly hidden: InputSignal<boolean> = input<boolean>(false);

  /**
   * Tooltip text displayed on hover.
   *
   * Applied as the HTML title attribute.
   *
   * @default undefined
   */
  readonly title: InputSignal<string | undefined> = input<string | undefined>(undefined);

  readonly tooltip: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Tooltip position relative to the icon.
   */
  readonly tooltipPosition = input<TooltipPosition>('top');

  /**
   * Tooltip color scheme.
   */
  readonly tooltipColor = input<TooltipColor>('info');
  /**
   * Whether the control should be shown in the designer.
   *
   * When false, the control is hidden from design tools and template builders.
   *
   * @default true
   */
  readonly showInDesigner: InputSignal<boolean> = input<boolean>(true);

  /**
   * Design mode indicator (true for template builder/designer, false for runtime).
   *
   * When true:
   *
   * - Control is displayed in design/builder mode
   * - May show additional configuration options
   * - May disable certain interactive features
   * - Used by template builders and design tools
   *
   * @default false
   */
  readonly designMode: InputSignal<boolean> = input<boolean>(false);

  /**
   * Control type classification for organizing controls by purpose.
   *
   * - BasicControl: Standard form controls and interactive elements (inputs, buttons, etc.)
   * - LayoutControl: Structural and layout components (sections, cards, dividers, etc.)
   * - ClinicalControl: Clinical calculation and medical-specific components
   *
   * @default ControlType.BasicControl
   */
  readonly controlType: InputSignal<ControlType> = input<ControlType>(ControlType.BasicControl);

  /**
   * Whether the control can be dragged and dropped in design/builder tools.
   *
   * When true:
   *
   * - Control can be dragged from a palette or toolbox
   * - Control can be dropped into design areas
   * - Used by template builders and drag-and-drop interfaces
   *
   * When false:
   *
   * - Control is excluded from drag-and-drop operations
   * - Useful for controls that should only be used programmatically
   *
   * @default true
   */
  readonly canDragDrop: InputSignal<boolean> = input<boolean>(true);

  // ===== Designer Interaction Control Properties =====

  /**
   * Whether the control can be selected in the designer.
   *
   * When false, the control cannot be selected or focused in design tools.
   *
   * @default true
   */
  readonly canSelect: InputSignal<boolean> = input<boolean>(true);

  /**
   * Whether the control can be moved/resized in the designer.
   *
   * When false, the control position is locked and cannot be moved.
   *
   * @default true
   */
  readonly canMove: InputSignal<boolean> = input<boolean>(true);

  /**
   * Whether the control can be resized in the designer.
   *
   * When true, the control can be resized by dragging handles in design tools.
   *
   * @default false
   */
  readonly canResize: InputSignal<boolean> = input<boolean>(false);

  /**
   * Whether the control is locked (prevents editing in designer).
   *
   * When true, the control cannot be modified, moved, or deleted in the designer.
   *
   * @default false
   */
  readonly isLocked: InputSignal<boolean> = input<boolean>(false);

  /**
   * Whether the control can be deleted from the designer.
   *
   * When false, the control cannot be removed from the design canvas.
   *
   * @default true
   */
  readonly canDelete: InputSignal<boolean> = input<boolean>(true);

  /**
   * Whether the control can be duplicated in the designer.
   *
   * When true, the control can be copied/duplicated in design tools.
   *
   * @default true
   */
  readonly canDuplicate: InputSignal<boolean> = input<boolean>(true);

  // ===== Metadata and Organization Properties =====

  /**
   * Display name shown in designer palette/toolbox.
   *
   * If not provided, the component selector or class name is used.
   *
   * @default undefined
   */
  readonly designerLabel: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Category for grouping in palette (e.g., "Form Controls", "Layout", "Clinical").
   *
   * Used to organize controls in the designer toolbox/palette.
   *
   * @default undefined
   */
  readonly designerCategory: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Tags for searching/filtering in designer.
   *
   * Array of strings used for searching and filtering controls in the designer.
   *
   * @default [ ]
   */
  readonly designerTags: InputSignal<string[]> = input<string[]>([]);

  /**
   * Icon name/class for palette display.
   *
   * Icon identifier or CSS class name to display in the designer palette.
   *
   * @default undefined
   */
  readonly designerIcon: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Description/tooltip shown in designer.
   *
   * Helpful text displayed when hovering over the control in the designer palette.
   *
   * @default undefined
   */
  readonly designerDescription: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Help text or documentation link for designer.
   *
   * URL to documentation or help content for this control in the designer.
   *
   * @default undefined
   */
  readonly designerHelpUrl: InputSignal<string | undefined> = input<string | undefined>(undefined);

  // ===== Size Constraints Properties =====

  /**
   * Minimum width in designer (CSS value: px, %, rem, etc.).
   *
   * Prevents the control from being resized smaller than this value.
   *
   * @default undefined
   */
  readonly minWidth: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Maximum width in designer (CSS value: px, %, rem, etc.).
   *
   * Prevents the control from being resized larger than this value.
   *
   * @default undefined
   */
  readonly maxWidth: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Minimum height in designer (CSS value: px, %, rem, etc.).
   *
   * Prevents the control from being resized smaller than this value.
   *
   * @default undefined
   */
  readonly minHeight: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Maximum height in designer (CSS value: px, %, rem, etc.).
   *
   * Prevents the control from being resized larger than this value.
   *
   * @default undefined
   */
  readonly maxHeight: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Preferred/default width in designer (CSS value: px, %, rem, etc.).
   *
   * Suggested width when the control is first added to the designer.
   *
   * @default undefined
   */
  readonly defaultWidth: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Preferred/default height in designer (CSS value: px, %, rem, etc.).
   *
   * Suggested height when the control is first added to the designer.
   *
   * @default undefined
   */
  readonly defaultHeight: InputSignal<string | undefined> = input<string | undefined>(undefined);

  // ===== Layout and Positioning Properties =====

  /**
   * Grid column span for grid layouts.
   *
   * CSS grid-column value (e.g., "1 / 3", "span 2").
   *
   * @default undefined
   */
  readonly gridColumn: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Grid row span for grid layouts.
   *
   * CSS grid-row value (e.g., "1 / 3", "span 2").
   *
   * @default undefined
   */
  readonly gridRow: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Flex order for flexbox layouts.
   *
   * CSS order value for controlling visual order in flex containers.
   *
   * @default undefined
   */
  readonly flexOrder: InputSignal<number | undefined> = input<number | undefined>(undefined);

  /**
   * Whether to snap to grid when moving.
   *
   * When true, the control snaps to grid positions when moved in the designer.
   *
   * @default true
   */
  readonly snapToGrid: InputSignal<boolean> = input<boolean>(true);

  /**
   * Grid size for snapping (in pixels).
   *
   * Size of the grid cells for snap-to-grid functionality.
   *
   * @default 8
   */
  readonly gridSize: InputSignal<number> = input<number>(8);

  // ===== Preview and Runtime Visibility Properties =====

  /**
   * Whether to show in preview mode.
   *
   * When false, the control is hidden in preview mode but may still be visible in designer.
   *
   * @default true
   */
  readonly showInPreview: InputSignal<boolean> = input<boolean>(true);

  /**
   * Whether to show in runtime (production).
   *
   * When false, the control is hidden in production but may be visible in designer/preview.
   *
   * @default true
   */
  readonly showInRuntime: InputSignal<boolean> = input<boolean>(true);

  /**
   * Placeholder content shown in designer when empty.
   *
   * Text or content displayed in the designer when the control has no value/content.
   *
   * @default undefined
   */
  readonly designerPlaceholder: InputSignal<string | undefined> = input<string | undefined>(undefined);

  // ===== Validation and Constraints Properties =====

  /**
   * Whether this control is required in the form.
   *
   * When true, the control must have a value for the form to be valid.
   *
   * @default false
   */
  readonly required: InputSignal<boolean> = input<boolean>(false);

  /**
   * Custom validation rules (JSON serializable).
   *
   * Object containing validation rules that can be serialized to JSON for template storage.
   *
   * @default undefined
   */
  readonly validationRules: InputSignal<Record<string, any> | undefined> = input<Record<string, any> | undefined>(
    undefined
  );

  /**
   * Whether control is read-only.
   *
   * When true, the control value cannot be modified by the user.
   *
   * @default false
   */
  readonly readonly: InputSignal<boolean> = input<boolean>(false);

  // ===== Label Styling Properties =====

  /**
   * Font weight for the label text. Options: 'Light', 'Regular', 'Medium', 'Semi Bold', 'Bold', 'Italic', 'Underline'
   *
   * @default 'Semi Bold'
   */
  readonly labelFontWeight: InputSignal<string | undefined> = input<string | undefined>('Semi Bold');

  /**
   * Font size for the label text in pixels.
   *
   * @default undefined (inherits from CSS)
   */
  readonly labelFontSize: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Font weight for the subtitle text. Options: 'Light', 'Regular', 'Medium', 'Semi Bold', 'Bold', 'Italic',
   * 'Underline'
   *
   * @default 'Regular'
   */
  readonly subtitleFontWeight: InputSignal<string | undefined> = input<string | undefined>('Regular');

  /**
   * Font size for the subtitle text in pixels.
   *
   * @default '14'
   */
  readonly subtitleFontSize: InputSignal<string | undefined> = input<string | undefined>('14');

  /**
   * Color for the label text. Accepts any valid CSS color value.
   *
   * @default undefined
   */
  readonly labelColor: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * Color for the subtitle text. Accepts any valid CSS color value.
   *
   * @default undefined
   */
  readonly subtitleColor: InputSignal<string | undefined> = input<string | undefined>(undefined);

  // ===== Serialization and Configuration Properties =====

  /**
   * Configuration data for template storage (JSON serializable).
   *
   * Object containing control-specific configuration that can be serialized to JSON.
   *
   * @default undefined
   */
  readonly designerConfig: InputSignal<Record<string, any> | undefined> = input<Record<string, any> | undefined>(
    undefined
  );

  /**
   * Whether this control's configuration can be edited.
   *
   * When false, the control's properties cannot be modified in the designer property panel.
   *
   * @default true
   */
  readonly configurable: InputSignal<boolean> = input<boolean>(true);

  /**
   * Default configuration values for new instances.
   *
   * Object containing default property values when a new instance is created in the designer.
   *
   * @default undefined
   */
  readonly defaultConfig: InputSignal<Record<string, any> | undefined> = input<Record<string, any> | undefined>(
    undefined
  );

  /**
   * Computed signal: determines if the control should be displayed.
   *
   * Uses the show() input to control visibility.
   *
   * @returns True if control is visible, false otherwise
   */
  readonly isVisible: Signal<boolean> = computed(() => this.show());

  /**
   * Computed signal: generates combined CSS classes from all class inputs.
   *
   * Combines:
   *
   * - CssClass input
   * - Any component-specific classes
   *
   * Override this in child components to add component-specific classes.
   *
   * @returns Space-separated CSS class string
   */
  protected readonly computedClasses: Signal<Record<string, boolean>> = computed(() => ({
    'hx-control--disabled': this.disabledState(),
    'hx-control--hidden': this.hidden(),
  }));

  /**
   * Gets the effective style string for the component.
   *
   * Can be overridden in child components to add component-specific styles.
   *
   * @returns CSS style string
   */
  protected getEffectiveStyle(): string {
    return this.cssStyle();
  }

  /**
   * Gets the computed CSS class string combining all class inputs.
   *
   * Can be overridden in child components to add component-specific class logic.
   *
   * @returns Space-separated CSS class string
   */
  protected getEffectiveClasses(): string {
    const classes = this.computedClasses();
    const classList = [this.cssClass()];

    Object.entries(classes).forEach(([className, shouldApply]) => {
      if (shouldApply) {
        classList.push(className);
      }
    });

    return classList.filter((c) => c).join(' ');
  }

  /**
   * Helper method to merge class objects.
   *
   * Useful in child components for combining base classes with component-specific classes.
   *
   * @example
   *   ```typescript
   *   protected readonly buttonClasses = computed(() =>
   *     this.mergeClasses(
   *       this.computedClasses(),
   *       {
   *         'hx-button': true,
   *         'hx-button--primary': this.isPrimary(),
   *       }
   *     )
   *   );
   *   ```;
   *
   * @param baseClasses - Base class object
   * @param additionalClasses - Additional classes to merge
   * @returns Merged class object
   */
  protected mergeClasses(
    baseClasses: Record<string, boolean>,
    additionalClasses: Record<string, boolean>
  ): Record<string, boolean> {
    return { ...baseClasses, ...additionalClasses };
  }

  // ===== ControlValueAccessor Implementation =====

  /**
   * Write value from form model to component Called when form programmatically sets value (e.g., form.patchValue()) Can
   * be overridden in subclasses for custom behavior
   *
   * @internal Form integration
   */
  writeValue(value: any): void {
    this._setValue(value);
  }

  /**
   * Register callback for value changes Called by forms API to register onChange handler
   *
   * @internal Form integration
   */
  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  /**
   * Register callback for touch events Called by forms API to register onTouched handler
   *
   * @internal Form integration
   */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /**
   * Set disabled state from form model Called when form.disable() or form.enable() is called Can be overridden for
   * custom behavior
   *
   * @internal Form integration
   */
  setDisabledState(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
    this._changeDetectorRef.markForCheck();
  }

  // ===== Validator Helper Methods =====
  // Note: These methods are provided as utilities for subclasses that implement Validator.
  // They are NOT part of the Validator interface on this base class.

  /**
   * Validate the control value. Called by forms to validate this control when the subclass implements Validator and
   * provides NG_VALIDATORS.
   *
   * @internal Form integration
   */
  validate(control: AbstractControl): ValidationErrors | null {
    return this._validate(control);
  }

  /**
   * Register callback for validator changes. Called by forms API to register validator change handler when the subclass
   * implements Validator.
   *
   * @internal Form integration
   */
  registerOnValidatorChange(fn: () => void): void {
    this._validatorChangeFn = fn;
  }

  // ===== Shared Form Control Methods =====

  /**
   * Mark control as touched Notifies form that user has interacted with the control
   *
   * @protected
   */
  protected markTouched(): void {
    this._onTouched();
  }

  /**
   * Notify form of value change Emits value to form model via ControlValueAccessor
   *
   * @param value - New value to emit
   * @protected
   */
  protected notifyChange(value: any): void {
    this._onChange(value);
  }

  /**
   * Notify form that validator has changed Triggers form validation re-run
   *
   * @protected
   */
  protected notifyValidatorChange(): void {
    this._validatorChangeFn();
  }

  /**
   * Syncs interaction/validation state from the external Angular form control to an internal control and local UI state
   * signals.
   *
   * Use this in wrapper controls that maintain internal controls (`_internalControl`) but need to preserve touched/
   * dirty/invalid state when components are recreated (for example, section-based navigation).
   */
  protected syncExternalControlState(
    internalControl: AbstractControl,
    state: {
      setInvalid: (invalid: boolean) => void;
      setTouched?: (touched: boolean) => void;
      setDirty?: (dirty: boolean) => void;
      syncDirty?: boolean;
    }
  ): void {
    const externalControl = this.ngControl?.control;
    if (!externalControl) {
      return;
    }

    if (externalControl.touched && !internalControl.touched) {
      internalControl.markAsTouched({ onlySelf: true });
    }

    if (state.syncDirty && externalControl.dirty && !internalControl.dirty) {
      internalControl.markAsDirty({ onlySelf: true });
    }

    state.setInvalid(externalControl.invalid);
    state.setTouched?.(externalControl.touched);
    state.setDirty?.(externalControl.dirty);
  }

  /**
   * Connects external form-control state to an internal control and keeps it synchronized for the lifetime of this
   * component.
   */
  protected connectExternalControlState(
    internalControl: AbstractControl,
    state: {
      setInvalid: (invalid: boolean) => void;
      setTouched?: (touched: boolean) => void;
      setDirty?: (dirty: boolean) => void;
      syncDirty?: boolean;
    }
  ): void {
    const applyState = () => this.syncExternalControlState(internalControl, state);

    applyState();

    const externalControl = this.ngControl?.control;
    if (!externalControl) {
      return;
    }

    merge(externalControl.valueChanges, externalControl.statusChanges)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        applyState();
      });
  }

  /**
   * Abstract method: set value from form model Subclasses override to implement value setting
   *
   * @param value - Value to set
   * @protected
   */
  protected _setValue(value: any): void {
    // Override in subclasses
  }

  /**
   * Abstract method: validate the control Subclasses override to implement validation
   *
   * @returns ValidationErrors or null
   * @protected
   */
  protected _validate(control: AbstractControl): ValidationErrors | null {
    return null; // No validation by default
  }
}
